"use client";

import {
  AlertTriangle,
  CheckCircle2,
  CircleAlert,
  Info,
  Loader2
} from "lucide-react";
import { useTheme } from "next-themes";
import type { ComponentProps, CSSProperties } from "react";
import { Toaster as Sonner } from "sonner";

type ToasterProps = ComponentProps<typeof Sonner>;
type ToasterTheme = NonNullable<ToasterProps["theme"]>;

const MAX_VISIBLE_TOASTS = 3;

const toasterStyle: CSSProperties & Record<`--${string}`, string> = {
  "--border-radius": "var(--radius)",
  "--normal-bg": "var(--popover)",
  "--normal-border": "var(--border)",
  "--normal-text": "var(--popover-foreground)"
};

function toToasterTheme(theme: string | undefined): ToasterTheme {
  return theme === "light" || theme === "dark" ? theme : "system";
}

/**
 * App-wide toast viewport. Render once, inside the `ThemeProvider`, and raise
 * toasts with `toast`, `toast.success` or `toast.error` from `sonner`.
 */
export function Toaster({ toastOptions, ...props }: ToasterProps) {
  const { theme } = useTheme();

  return (
    <Sonner
      className="toaster group"
      closeButton
      icons={{
        error: <AlertTriangle className="size-4 text-destructive" />,
        info: <Info className="size-4" />,
        loading: <Loader2 className="size-4 animate-spin" />,
        success: <CheckCircle2 className="size-4 text-secondary" />,
        warning: <CircleAlert className="size-4 text-amber-500" />
      }}
      position="top-center"
      style={toasterStyle}
      theme={toToasterTheme(theme)}
      toastOptions={{
        ...toastOptions,
        classNames: {
          actionButton: "bg-primary! text-primary-foreground!",
          cancelButton: "bg-muted! text-muted-foreground!",
          description: "text-muted-foreground!",
          error: "border-t-4! border-t-destructive!",
          success: "border-t-4! border-t-secondary!",
          toast: "shadow-lg!",
          ...toastOptions?.classNames
        }
      }}
      visibleToasts={MAX_VISIBLE_TOASTS}
      {...props}
    />
  );
}
